import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, take } from 'rxjs';
import { AuthService, UserService } from '@lumio/shared';
import { Capacitor } from '@capacitor/core';
import { ROUTE_HOME } from '../../data/models/global-routes';

/**
 * Guard for the public landing page.
 *
 * On native (iOS/Android) always skips the landing and goes straight to /app
 * so the domain guards can decide the correct screen (onboarding, home, etc.).
 *
 * On browser: silently checks whether the user already has a valid session.
 * If so, redirects straight to the app home — preserving their session instead
 * of making them log in again (common after returning from Stripe or any
 * external redirect). If the check fails or there is no token, the landing
 * page is shown as usual (no cleanup needed here).
 */
export const activeSessionRedirectGuard: CanActivateFn = () => {
  const router = inject(Router);

  if (Capacitor.isNativePlatform()) {
    return router.parseUrl(ROUTE_HOME);
  }

  const auth = inject(AuthService);
  const userService = inject(UserService);

  if (!auth.isAuthenticated()) {
    return true;
  }

  return userService.verifySessionActive().pipe(
    take(1),
    map(() => router.parseUrl(ROUTE_HOME)),
    catchError(() => of(true as const)),
  );
};
